import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Save, X } from "lucide-react";
import { eCourseService } from "../../services/elearning/eCourseService";
import type {
  ECourse,
  ECourseFormData,
} from "../../services/elearning/eCourseService";
import toast from "react-hot-toast";

const TeacherCourseEdit: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [course, setCourse] = useState<ECourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  // Check if we're in admin context
  const isAdminContext = location.pathname.includes("/admin/");

  const [formData, setFormData] = useState<ECourseFormData>({
    title: "",
    description: "",
    shortDescription: "",
    thumbnail: "",
    category: "Other",
    level: "Beginner",
    language: "English",
    duration: {
      estimatedHours: 0,
      estimatedMinutes: 0,
    },
    pricing: {
      type: "free",
      amount: 0,
      currency: "KES",
    },
    registration: {
      type: "self",
      requiresApproval: false,
      maxStudents: undefined,
    },
    settings: {
      allowDiscussions: true,
      allowDownloads: false,
      trackProgress: true,
      generateCertificate: false,
    },
    tags: [],
    visibility: "public",
    chain: {
      chainId: "",
      sequenceNumber: 1,
      isChainFinal: true,
      nextCourseId: "",
    },
  });

  const categories = [
    "Programming",
    "Design",
    "Business",
    "Marketing",
    "Language",
    "Science",
    "Mathematics",
    "Arts",
    "Health",
    "Technology",
    "Other",
  ];

  const languages = [
    "English",
    "Spanish",
    "French",
    "German",
    "Italian",
    "Portuguese",
    "Chinese",
    "Japanese",
    "Korean",
    "Somali",
    "Arabic",
    "Hindi",
    "Russian",
    "Swahili",
    "Other",
  ];

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await eCourseService.getCourse(courseId!);
      if (response.success) {
        setCourse(response.data);
        // Populate form with existing data
        const courseData = response.data;
        setFormData({
          title: courseData.title || "",
          description: courseData.description || "",
          shortDescription: courseData.shortDescription || "",
          thumbnail: courseData.thumbnail || "",
          category: courseData.category || "Other",
          level: courseData.level || "Beginner",
          language: courseData.language || "English",
          duration: {
            estimatedHours: courseData.duration?.estimatedHours || 0,
            estimatedMinutes: courseData.duration?.estimatedMinutes || 0,
          },
          pricing: {
            type: courseData.pricing?.type || "free",
            amount: courseData.pricing?.amount || 0,
            currency: courseData.pricing?.currency || "KES",
          },
          registration: {
            type: courseData.registration?.type || "self",
            requiresApproval:
              courseData.registration?.requiresApproval || false,
            maxStudents: courseData.registration?.maxStudents,
          },
          settings: {
            allowDiscussions: courseData.settings?.allowDiscussions ?? true,
            allowDownloads: courseData.settings?.allowDownloads ?? false,
            trackProgress: courseData.settings?.trackProgress ?? true,
            generateCertificate:
              courseData.settings?.generateCertificate ?? false,
          },
          tags: courseData.tags || [],
          visibility: courseData.visibility || "public",
          chain: {
            chainId: courseData.chain?.chainId || "",
            sequenceNumber: courseData.chain?.sequenceNumber || 1,
            isChainFinal: courseData.chain?.isChainFinal ?? true,
            nextCourseId: courseData.chain?.nextCourseId || "",
          },
        });

        // Set thumbnail preview if exists
        if (courseData.thumbnail) {
          setThumbnailPreview(courseData.thumbnail);
        }
      } else {
        setError("Failed to load course");
      }
    } catch (err) {
      console.error("Error loading course:", err);
      setError("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId) return;

    try {
      setSaving(true);

      // Upload thumbnail if a new file was selected
      if (thumbnailFile) {
        const uploadResponse = await eCourseService.uploadThumbnail(
          courseId,
          thumbnailFile
        );
        if (uploadResponse.success) {
          // Update formData with the new thumbnail URL
          formData.thumbnail = uploadResponse.data.thumbnail;
        } else {
          setError("Failed to upload thumbnail");
          return;
        }
      }

      const response = await eCourseService.updateCourse(courseId, formData);
      if (response.success) {
        // Reload the course data to show updated values
        await loadCourse();
        toast.success("Course updated successfully!");
      } else {
        toast.error("Failed to update course");
      }
    } catch (err) {
      console.error("Error updating course:", err);
      setError("Failed to update course");
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const tagInput = (document.getElementById("tagInput") as HTMLInputElement)
      ?.value;
    if (tagInput?.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      (document.getElementById("tagInput") as HTMLInputElement).value = "";
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((tag) => tag !== tagToRemove) || [],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
            <div className="h-64 bg-gray-300 rounded mb-6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-red-600 mb-4">{error || "Course not found"}</p>
          <button
            onClick={() =>
              navigate(isAdminContext ? `/admin/elearning` : "/elearning")
            }
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() =>
                navigate(
                  isAdminContext
                    ? `/admin/elearning`
                    : `/elearning/courses/${courseId}`
                )
              }
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Course
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  navigate(
                    isAdminContext
                      ? `/admin/elearning`
                      : `/elearning/courses/${courseId}`
                  )
                }
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Basic Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Short Description
                </label>
                <input
                  type="text"
                  value={formData.shortDescription}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      shortDescription: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Thumbnail
                </label>
                <div className="space-y-2">
                  {thumbnailPreview || formData.thumbnail ? (
                    <div className="relative">
                      <img
                        src={thumbnailPreview || formData.thumbnail}
                        alt="Course thumbnail"
                        className="w-full max-w-sm h-32 object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setThumbnailFile(null);
                          setThumbnailPreview(null);
                          setFormData((prev) => ({ ...prev, thumbnail: "" }));
                        }}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ) : null}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setThumbnailFile(file);
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setThumbnailPreview(e.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500">
                    Upload a thumbnail image for your course (recommended:
                    1280x720px)
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Description *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Level
                  </label>
                  <select
                    value={formData.level}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        level: e.target.value as any,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        language: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    {languages.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Duration
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hours
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration?.estimatedHours || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: {
                        ...prev.duration!,
                        estimatedHours:
                          e.target.value === ""
                            ? ""
                            : parseInt(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minutes
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.duration?.estimatedMinutes || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: {
                        ...prev.duration!,
                        estimatedMinutes:
                          e.target.value === ""
                            ? ""
                            : parseInt(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Pricing
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Type
                </label>
                <select
                  value={formData.pricing?.type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      pricing: {
                        ...prev.pricing!,
                        type: e.target.value as "free" | "paid",
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              {formData.pricing?.type === "paid" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (KES)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing?.amount || 0}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pricing: {
                          ...prev.pricing!,
                          amount: parseFloat(e.target.value) || 0,
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter price in Kenyan Shillings"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Tags</h2>
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  id="tagInput"
                  type="text"
                  placeholder="Add a tag"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addTag())
                  }
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags?.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Course Chain Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Course Series (Optional)
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Group this course with others in a sequential learning series.
              Students must complete courses in order.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Series ID
                </label>
                <input
                  type="text"
                  value={formData.chain?.chainId || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      chain: {
                        ...prev.chain!,
                        chainId: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., graphic-design-series"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Unique identifier for the course series
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sequence Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.chain?.sequenceNumber || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = value === "" ? "" : parseInt(value) || 1;
                      setFormData((prev) => ({
                        ...prev,
                        chain: {
                          ...prev.chain!,
                          sequenceNumber: numValue,
                        },
                      }));
                    }}
                    placeholder="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Order in the series (1, 2, 3...)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Is Final Course?
                  </label>
                  <select
                    value={formData.chain?.isChainFinal ? "true" : "false"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        chain: {
                          ...prev.chain!,
                          isChainFinal: e.target.value === "true",
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="false">No - Intermediate course</option>
                    <option value="true">
                      Yes - Final course (issues certificate)
                    </option>
                  </select>
                </div>
              </div>

              {formData.chain?.chainId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Series Configuration
                  </h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>
                      <strong>Series:</strong> {formData.chain.chainId}
                    </p>
                    <p>
                      <strong>Position:</strong> Part{" "}
                      {formData.chain.sequenceNumber}
                    </p>
                    <p>
                      <strong>Certificate:</strong>{" "}
                      {formData.chain.isChainFinal
                        ? "Issued upon completion"
                        : "Not issued (intermediate course)"}
                    </p>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Students must complete all previous courses in this series
                    before enrolling in this one.
                  </p>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherCourseEdit;
